import { useCallback, useEffect, useRef, useState } from 'react';

import { Box, Button, ChevronLeftOutline, ChevronRightOutline, HStack, IconButton, VStack } from '@bonusly/design-system';

const PROMPT_GRADIENT = 'linear-gradient(130deg, #C5E8BA 0%, #70C481 24%, #B3EBE8 55%, #9783EA 100%)';

const gradientBorderSx = {
	border: '1px solid transparent',
	backgroundImage: `linear-gradient(var(--chakra-colors-bg-surface), var(--chakra-colors-bg-surface)), ${PROMPT_GRADIENT}`,
	backgroundOrigin: 'padding-box, border-box',
	backgroundClip: 'padding-box, border-box',
	'&:hover': {
		border: '1px solid transparent',
		backgroundImage: `linear-gradient(var(--chakra-colors-bg-subtle), var(--chakra-colors-bg-subtle)), ${PROMPT_GRADIENT}`,
	},
};

interface PromptButtonProps {
	prompt: string;
	onSelect: () => void;
	nowrap?: boolean;
}

function PromptButton({ prompt, onSelect, nowrap }: PromptButtonProps) {
	return (
		<Button
			variant="outline"
			size="sm"
			borderRadius="12px"
			color="text.softest"
			fontWeight="normal"
			fontSize="sm"
			h="auto"
			py={1.5}
			px={3}
			whiteSpace={nowrap ? 'nowrap' : 'normal'}
			textAlign="left"
			flexShrink={nowrap ? 0 : undefined}
			onClick={onSelect}
			sx={gradientBorderSx}
			_hover={{ color: 'text.default' }}
		>
			{prompt}
		</Button>
	);
}

const SCROLL_AMOUNT = 200;

interface HorizontalPromptsProps {
	prompts: string[];
	onSelectPrompt: (prompt: string, index: number) => void;
	/** Use negative horizontal margins to bleed into parent padding (default: true) */
	bleed?: boolean;
	/** Bottom margin on the container (default: 2) */
	mb?: number;
}

export function HorizontalPrompts({ prompts, onSelectPrompt, bleed = true, mb: marginBottom = 2 }: HorizontalPromptsProps) {
	const scrollRef = useRef<HTMLDivElement>(null);
	const [canScrollLeft, setCanScrollLeft] = useState(false);
	const [canScrollRight, setCanScrollRight] = useState(false);

	const updateScrollState = useCallback(() => {
		const el = scrollRef.current;
		if (!el) return;
		setCanScrollLeft(el.scrollLeft > 0);
		setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
	}, []);

	useEffect(() => {
		updateScrollState();
		const el = scrollRef.current;
		if (!el) return;
		const observer = new ResizeObserver(updateScrollState);
		observer.observe(el);
		return () => observer.disconnect();
	}, [updateScrollState, prompts]);

	const scroll = useCallback((direction: 'left' | 'right') => {
		scrollRef.current?.scrollBy({ left: direction === 'left' ? -SCROLL_AMOUNT : SCROLL_AMOUNT, behavior: 'smooth' });
	}, []);

	return (
		<Box position="relative" mx={bleed ? -2 : 0} mb={marginBottom} overflow={bleed ? undefined : 'hidden'}>
			<Box
				ref={scrollRef}
				overflowX="auto"
				onScroll={updateScrollState}
				sx={{
					maskImage: `linear-gradient(to right, ${canScrollLeft ? 'transparent' : 'black'}, black 16px, black calc(100% - 16px), ${canScrollRight ? 'transparent' : 'black'})`,
					WebkitMaskImage: `linear-gradient(to right, ${canScrollLeft ? 'transparent' : 'black'}, black 16px, black calc(100% - 16px), ${canScrollRight ? 'transparent' : 'black'})`,
					scrollbarWidth: 'none',
					'&::-webkit-scrollbar': { display: 'none' },
				}}
			>
				<HStack spacing={2} px={2} py={1}>
					{prompts.map((prompt, index) => (
						<PromptButton key={`${index}-${prompt}`} prompt={prompt} onSelect={() => onSelectPrompt(prompt, index)} nowrap />
					))}
				</HStack>
			</Box>

			{canScrollLeft && (
				<IconButton
					aria-label="Scroll left"
					icon={<ChevronLeftOutline boxSize={3} />}
					size="xs"
					variant="outline"
					isRound
					position="absolute"
					left={0}
					top="50%"
					transform="translateY(-50%)"
					bg="bg.surface"
					borderColor="border.soft"
					color="text.soft"
					boxSize={6}
					minW={0}
					p={3}
					opacity={0}
					transition="opacity 0.15s ease-in-out"
					onClick={() => scroll('left')}
					_hover={{ opacity: 1, bg: 'bg.subtle', color: 'text.default' }}
				/>
			)}

			{canScrollRight && (
				<IconButton
					aria-label="Scroll right"
					icon={<ChevronRightOutline boxSize={3} />}
					size="xs"
					variant="outline"
					isRound
					position="absolute"
					right={0}
					top="50%"
					transform="translateY(-50%)"
					bg="bg.surface"
					borderColor="border.soft"
					color="text.soft"
					boxSize={6}
					minW={0}
					p={3}
					opacity={0}
					transition="opacity 0.15s ease-in-out"
					onClick={() => scroll('right')}
					_hover={{ opacity: 1, bg: 'bg.subtle', color: 'text.default' }}
				/>
			)}
		</Box>
	);
}

interface PromptChipsProps {
	prompts: string[];
	onSelectPrompt: (prompt: string, index: number) => void;
	layout?: 'vertical' | 'horizontal';
}

export function PromptChips({ prompts, onSelectPrompt, layout = 'vertical' }: PromptChipsProps) {
	if (prompts.length === 0) return null;

	if (layout === 'horizontal') {
		return <HorizontalPrompts prompts={prompts} onSelectPrompt={onSelectPrompt} />;
	}

	return (
		<VStack spacing={1} align="flex-start" mb={2}>
			{prompts.map((prompt, index) => (
				<PromptButton key={`${index}-${prompt}`} prompt={prompt} onSelect={() => onSelectPrompt(prompt, index)} />
			))}
		</VStack>
	);
}
